/** Right-side detail panel that opens when the user clicks a node on
 *  the canvas. Shows the node's label, type chip, raw props, and the
 *  list of incoming + outgoing edges by type, plus an "open in
 *  workspace" affordance for note / task / project nodes. */
import { Link } from "react-router-dom";
import { ExternalLink, X, Layers } from "lucide-react";
import type { ApiEdge, ApiNode } from "./types";
import { nodeStyleFor } from "./palette";

export function DetailPanel({
  node,
  edges,
  nodesById,
  onClose,
  onSelect,
}: {
  node: ApiNode;
  edges: ApiEdge[];
  nodesById: Map<string, ApiNode>;
  onClose: () => void;
  onSelect: (id: string) => void;
}) {
  const style = nodeStyleFor(node.type);
  const incoming = edges.filter((e) => e.to === node.id);
  const outgoing = edges.filter((e) => e.from === node.id);

  const workspaceHref = workspaceLinkFor(node);

  return (
    <aside
      role="dialog"
      aria-label={`Details for ${node.label}`}
      className="w-full sm:w-96 h-full flex-shrink-0 border-l border-slate-200 dark:border-slate-700/60 bg-white/95 dark:bg-slate-900/95 backdrop-blur overflow-y-auto"
    >
      <header className="sticky top-0 z-10 flex items-start justify-between gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-700/60 bg-white/80 dark:bg-slate-900/80 backdrop-blur">
        <div className="flex-1 min-w-0">
          <div
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full"
            style={{
              background: style.fill,
              color: style.text,
              border: `1px solid ${style.border}`,
            }}
          >
            <span
              aria-hidden
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: style.accent }}
            />
            {node.type}
          </div>
          <h3 className="mt-1 text-base font-bold text-slate-900 dark:text-slate-100 truncate">
            {node.label}
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close detail panel"
          className="flex-shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          <X className="w-4 h-4" />
        </button>
      </header>

      <div className="px-4 py-4 space-y-5">
        {workspaceHref && (
          <Link
            to={workspaceHref}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-600 dark:text-sky-400 hover:underline"
          >
            <ExternalLink className="w-4 h-4" />
            Open in workspace
          </Link>
        )}

        <Section title="Connections" empty="No connections yet.">
          {(incoming.length > 0 || outgoing.length > 0) && (
            <ul className="space-y-1.5">
              {outgoing.map((e) => (
                <EdgeRow
                  key={e.id}
                  edge={e}
                  direction="out"
                  other={nodesById.get(e.to)}
                  onSelect={onSelect}
                />
              ))}
              {incoming.map((e) => (
                <EdgeRow
                  key={e.id}
                  edge={e}
                  direction="in"
                  other={nodesById.get(e.from)}
                  onSelect={onSelect}
                />
              ))}
            </ul>
          )}
        </Section>

        {Object.keys(node.props ?? {}).length > 0 && (
          <Section title="Properties">
            <dl className="text-xs grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1">
              {Object.entries(node.props).map(([k, v]) => (
                <PropRow key={k} k={k} v={v} />
              ))}
            </dl>
          </Section>
        )}

        <Section title="Identity">
          <dl className="text-xs grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1">
            <dt className="text-slate-500 dark:text-slate-400">Created</dt>
            <dd className="text-slate-700 dark:text-slate-200 truncate">
              {formatDate(node.createdAt)}
            </dd>
            <dt className="text-slate-500 dark:text-slate-400">Updated</dt>
            <dd className="text-slate-700 dark:text-slate-200 truncate">
              {formatDate(node.updatedAt)}
            </dd>
            <dt className="text-slate-500 dark:text-slate-400">Id</dt>
            <dd className="text-slate-700 dark:text-slate-200 font-mono text-[10px] truncate">
              {node.id}
            </dd>
          </dl>
        </Section>
      </div>
    </aside>
  );
}

function Section({
  title,
  empty,
  children,
}: {
  title: string;
  empty?: string;
  children?: React.ReactNode;
}) {
  const hasChildren = !!children;
  return (
    <section>
      <h4 className="text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-1.5">
        <Layers className="w-3 h-3" />
        {title}
      </h4>
      {hasChildren ? (
        children
      ) : (
        <p className="text-xs text-slate-500 dark:text-slate-400 italic">{empty}</p>
      )}
    </section>
  );
}

function EdgeRow({
  edge,
  direction,
  other,
  onSelect,
}: {
  edge: ApiEdge;
  direction: "in" | "out";
  other: ApiNode | undefined;
  onSelect: (id: string) => void;
}) {
  if (!other) return null;
  const style = nodeStyleFor(other.type);
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(other.id)}
        className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/40 transition-colors"
      >
        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 w-12 flex-shrink-0">
          {direction === "out" ? "→" : "←"} {edge.type}
        </span>
        <span
          aria-hidden
          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
          style={{ background: style.accent }}
        />
        <span className="text-sm text-slate-800 dark:text-slate-100 truncate flex-1">
          {other.label}
        </span>
      </button>
    </li>
  );
}

function PropRow({ k, v }: { k: string; v: unknown }) {
  const text =
    typeof v === "string"
      ? v
      : typeof v === "number" || typeof v === "boolean"
        ? String(v)
        : JSON.stringify(v);
  return (
    <>
      <dt className="text-slate-500 dark:text-slate-400">{k}</dt>
      <dd className="text-slate-700 dark:text-slate-200 truncate">{text}</dd>
    </>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/** Map a graph node to its workspace location, when one exists. The
 *  type-specific routes haven't all landed yet; we hand back null for
 *  types that can't be opened anywhere yet, and the panel hides the
 *  "open in workspace" CTA for those. */
function workspaceLinkFor(node: ApiNode): string | null {
  switch (node.type) {
    case "task":
      return `/backlog`;
    case "note":
    case "project":
    case "idea":
    case "source":
      return `/explore`;
    default:
      return null;
  }
}
